import React, { useState, useEffect, useRef } from 'react';
import { Card, CardBody, Col, Container, Input, Spinner, Badge } from 'reactstrap';
import { AlertCircle, CheckCircle, Clock } from 'react-feather';
import { apiPost } from '../../Utils';
import Breadcrumbs from '../../CommonElements/Breadcrumbs';
import DataTable from 'react-data-table-component';
import { useAuth } from '../../Context/AuthContext';
import { H3 } from '../../AbstractElements';

interface Props {
    type: 'License' | 'RC' | 'Permit' | 'Insurance' | 'Service';
    title: string;
}

const UpcomingRenewals: React.FC<Props> = ({ type, title }) => {
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState<any[]>([]);
    const { hasPermission } = useAuth();

    // Filters
    const [searchTerm, setSearchTerm] = useState('');

    // Pagination & Sort State
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [sortColumn, setSortColumn] = useState('date');
    const [sortDirection, setSortDirection] = useState('asc'); // Default Ascending (Urgent First)

    const prevSearch = useRef(searchTerm);

    const fetchReport = (page: number, size: number, sortCol: string, sortDir: string) => {
        setLoading(true);
        const payload = {
            type: type, // Fixed type
            page: page,
            per_page: size,
            sort_by: sortCol,
            sort_order: sortDir,
            search: searchTerm
        };

        apiPost("reports/renewal", payload)
            .then((res: any) => {
                setReportData(res.data || []);
                console.log(res.data)
                setTotalRows(res.total || 0);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setLoading(false);
            });
    };

    useEffect(() => {
        setSearchTerm('');
        setCurrentPage(1);
    }, [type]);

    useEffect(() => {
        const isSearchChanged = prevSearch.current !== searchTerm;
        prevSearch.current = searchTerm;

        if (isSearchChanged && currentPage !== 1) {
            setCurrentPage(1);
            return;
        }

        const delay = isSearchChanged ? 500 : 0;
        const timer = setTimeout(() => {
            fetchReport(currentPage, perPage, sortColumn, sortDirection);
        }, delay);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, perPage, sortColumn, sortDirection, searchTerm, type]);

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePerRowsChange = (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSort = (column: any, sortDirection: string) => {
        const field = column.sortField || 'date';
        setSortColumn(field);
        setSortDirection(sortDirection);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const columns = [
        {
            name: <div>Sl.No</div>,
            cell: (row: any, index: number) => (currentPage - 1) * perPage + index + 1,
            width: "100px"
        },
        {
            name: <div>{type === 'License' ? 'Driver Name' : 'Reg No'}</div>,
            selector: (row: any) => row.vehicle_name,
            sortable: true,
            sortField: 'vehicle_name',
            wrap: true,
            cell: (row: any) => (
                <span>
                    <span className="text-primary fw-bold">{row.vehicle_name}</span>
                    {row.vehicle_deleted_at && <span className="text-danger small ms-1">(Deleted)</span>}
                </span>
            )
        },
        {
            name: <div>Branch</div>,
            selector: (row: any) => row.branch,
            sortable: true,
            sortField: 'branch',
            wrap: true,
            cell: (row: any) => (
                <span>
                    {row.branch}
                    {row.branch_deleted_at && <span className="text-danger small ms-1">(Deleted)</span>}
                </span>
            )
        },
        {
            name: <div>Due Date</div>,
            selector: (row: any) => row.date,
            sortable: true,
            sortField: 'date',
            cell: (row: any) => <span className="text-secondary fw-bold">{row.date ? new Date(row.date).toLocaleDateString('en-GB') : '-'}</span>
        },
        {
            name: <div>Status</div>,
            selector: (row: any) => row.status_label,
            sortable: false,
            cell: (row: any) => {
                if (row.status_label === 'Overdue') {
                    return <Badge color="danger" className="px-2 py-1"><AlertCircle size={12} className="me-1" /> Overdue</Badge>;
                } else if (String(row.status_label).startsWith('Due Soon')) {
                    return <Badge color="warning" className="text-dark px-2 py-1"><Clock size={12} className="me-1" /> {row.status_label}</Badge>;
                } else {
                    return <Badge color="success" className="px-2 py-1"><CheckCircle size={12} className="me-1" /> {row.status_label || 'Active'}</Badge>;
                }
            },
            width: '180px'
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: {
                fontSize: '15px',
                fontWeight: '600',
                paddingLeft: '16px',
                paddingRight: '16px',
            },
        },
        rows: {
            style: {
                minHeight: '50px',
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };

    return (
        <div className="page-body">
            <Breadcrumbs parent="Renewal" title={title} pageTitle={title} />
            <Container fluid>
                <Col xl={12} className="mb-lg-3">
                    <Card>
                        <CardBody>
                            <Col xs={12} className="mt-2 d-flex justify-content-between align-items-center mb-3">
                                <H3 className="text-primary">{title} Overview</H3>
                            </Col>

                            <Col xs={12} className="mb-3 d-flex justify-content-end">
                                <Input
                                    type="text"
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    style={{ maxWidth: "300px" }}
                                />
                            </Col>

                            <div className="table-responsive">
                                <DataTable
                                    columns={columns}
                                    data={reportData}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={totalRows}
                                    paginationDefaultPage={currentPage}
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={handlePerRowsChange}
                                    sortServer
                                    onSort={handleSort}
                                    progressPending={loading}
                                    progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                    highlightOnHover
                                    responsive
                                    customStyles={customStyles}
                                    noDataComponent={
                                        <div className="py-5 text-center text-muted">
                                            <p className="mb-0">No upcoming {title.toLowerCase()} found</p>
                                        </div>
                                    }
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Container>
        </div>
    );
};

export default UpcomingRenewals;
